import useGetDashboardProposalStats from '@/api/queries/dashboard/useGetDashboardProposalStats';
import { useTranslation } from '@/i18n';
import { useAccount } from '@/utils/storeHooks';
import { FC } from 'react';
import { DateRange } from '../utils';
import DashboardSectionEmpty from './DashboardSectionEmpty';
import DashboardSectionError from './DashboardSectionError';
import DashboardSectionSkeleton from './DashboardSectionSkeleton';
import DashboardStatsCard from './DashboardStatsCard';

interface DashboardProposalsProps {
  dateRange?: DateRange;
}

const DashboardProposals: FC<DashboardProposalsProps> = ({ dateRange }) => {
  const account = useAccount();
  const translate = useTranslation('dashboard.proposals');
  const translateErrors = useTranslation('dashboard.errors');
  const { stats, isLoading, isError } = useGetDashboardProposalStats({
    dateRange,
  });

  if (isLoading) {
    return <DashboardSectionSkeleton variant="stats-card" />;
  }

  if (isError) {
    return (
      <DashboardSectionError message={translateErrors('proposalsError')} />
    );
  }

  if (!stats) {
    return (
      <DashboardSectionEmpty
        title={translate('title')}
        message={translate('empty')}
      />
    );
  }

  return (
    <DashboardStatsCard
      title={translate('title')}
      tooltip={translate('titleTooltip')}
      region={account?.region}
      stats={[
        {
          key: 'sent',
          label: translate('sent'),
          color: '#3b82f6', // blue-500
          data: stats.sent,
          tooltip: translate('sentTooltip'),
        },
        {
          key: 'accepted',
          label: translate('accepted'),
          color: '#22c55e', // green-500
          data: stats.accepted,
          showRate: true,
          tooltip: translate('acceptedTooltip'),
        },
        {
          key: 'declined',
          label: translate('declined'),
          color: '#ef4444', // red-500
          data: stats.declined,
          showRate: true,
          tooltip: translate('declinedTooltip'),
        },
      ]}
      timeSeries={stats.timeSeries}
      chartSeries={[
        {
          name: translate('sent'),
          countKey: 'sentCount',
          valueKey: 'sentValue',
          color: '#3b82f6', // blue-500
        },
        {
          name: translate('accepted'),
          countKey: 'acceptedCount',
          valueKey: 'acceptedValue',
          color: '#22c55e', // green-500
        },
        {
          name: translate('declined'),
          countKey: 'declinedCount',
          valueKey: 'declinedValue',
          color: '#ef4444', // red-500
        },
      ]}
      rateSeries={{
        name: translate('acceptanceRate'),
        numeratorKey: 'acceptedCount',
        denominatorKey: 'sentCount',
        color: '#a855f7', // purple-500
      }}
      xAxisTitle={translate('xAxisTitle')}
    />
  );
};

export default DashboardProposals;
